<template>
  <div class="component">
    <div class="title-bar">
      <h1>{{ scopedStrings.title }}</h1>

      <button id="editor-reset-size" :title="scopedStrings.fontSizeResetTooltip" @click="resetFontSize()">
        <i :class="'fa fa-redo-alt'"></i>
      </button>

      <button :disabled="currentFontSize <= minFontSize" @click="decrementFontSize">
        <i :class="'fa fa-minus'"></i>
      </button>

      <input
        type="number"
        id="font-size-input"
        :min="minFontSize"
        :max="maxFontSize"
        v-model.number="currentFontSize"
        @change="updateFontSize()"
      >

      <button :disabled="currentFontSize >= maxFontSize" @click="incrementFontSize">
        <i :class="'fa fa-plus'"></i>
      </button>
    </div>
    <div></div>
  </div>
</template>

<script>
  import brace from 'brace'

  export default {
    name:"Editor",
    props: {
      scopedStrings: {
        type: Object,
        required: true
      },
      mode: {
        type: String,
        required: true
      },
      compiler: {
        type: Object,
        required: true
      }
    },
    data() {
      return {
        minFontSize: 8,
        maxFontSize: 48,
        currentFontSize: 16,
        defaultFontSize: 16
      }
    },
    methods: {
      setInstructionRecognitionRegex(instructionNames) {
        let newRegex = instructionNames.join("|")
        this.editor.session.$mode.$highlightRules.$rules.start[3].regex = newRegex
        // force recreation of tokenizer
        this.editor.session.$mode.$tokenizer = null
        this.editor.session.bgTokenizer.setTokenizer(this.editor.session.$mode.getTokenizer())
        // force re-highlight whole document
        this.editor.session.bgTokenizer.start(0)
        // update annotations
        this.deferredAnnotate()
      },
      updateEditor(){
        this.editor.resize(true)
      },
      getCode () {
        return this.editor.getValue()
      },
      setCode(code) {
        this.editor.setValue(code, -1)
      },
      updateFontSize(){
        if(!this.currentFontSize || this.currentFontSize < this.minFontSize || this.currentFontSize > this.maxFontSize){
          this.currentFontSize = this.editor.getFontSize()
        }
        this.editor.setFontSize(this.currentFontSize)
      },
      incrementFontSize() {
        this.currentFontSize++
        this.updateFontSize()
      },
      decrementFontSize() {
        this.currentFontSize--
        this.updateFontSize()
      },
      resetFontSize(){
        this.currentFontSize = this.defaultFontSize
        this.editor.setFontSize(this.currentFontSize)
      },
      deferredAnnotate () {
        if (this.annotateTimeout) {
          window.clearTimeout(this.annotateTimeout)
        }
        this.annotateTimeout = window.setTimeout(this.annotate, 500)
      },
      annotate () {
        try {
          let errors = this.compiler.assemble(this.editor.getValue()).errors;
          let displayableErrors = errors.filter(error => typeof(error.message) === "string")
          let aceFormattedErrors = displayableErrors.map(function (error) {
            return {
              row:    error.line - 1,
              column: 0,
              text:   error.message,
              type:   'error'
            }
          })
          this.editor.getSession().setAnnotations(aceFormattedErrors)
        } catch(e) {
          this.editor.getSession().setAnnotations([{
            row:    0,
            column: 0,
            text:   'Unknown error : ' + e,
            type:   'error',
          }])
        }
      }
    },
    mounted () {
      // Which element of the template should contain the editor
      this.editor = brace.edit(this.$el.childNodes[1])

      // NOTE This only works for our home-made modes, builtins modes cannot be
      // loaded this way, use `brace/mode/${this.mode}` instead
      require(`@/assets/brace/mode/${this.mode}`)
      require('@/assets/brace/theme/y86_sim')

      this.editor.setOptions({
        displayIndentGuides: true,
        fixedWidthGutter: true,
        highlightActiveLine: true,
        highlightGutterLine: true,
        mode: `ace/mode/${this.mode}`,
        theme: 'ace/theme/y86_sim',
        newLineMode: 'unix',
        printMargin: 80,
        readOnly: false,
        showGutter: true,
        tabSize: 4,
        useSoftTabs: true,
        wrap: true,
        fontSize: this.defaultFontSize
      })

      this.editor.setValue("", 1)
      this.editor.gotoLine(0);

      // Enables annotations on input errors
      this.editor.on('change', this.deferredAnnotate)
      // Removes a warning that pollutes the logs
      this.editor.$blockScrolling = Infinity;

      this.currentFontSize = this.defaultFontSize
    }
  }
</script>

<style scoped>
  .component > div {
    display: flex;
    flex-direction: column;
  }

  .component > .title-bar {
    background-color: var(--component-header-background);
    display: flex;
    flex-direction: row;
    flex-grow: 0;
    padding-right: 14px;
  }

  .component > .title-bar > h1 {
    flex-grow: 1;
  }

  .title-bar > button {
    background-color: var(--Editor-button-enabled-background);
    color: var(--Editor-button-enabled-foreground);
    margin: 4px 0 4px 0;
    width: 28px;
  }

  .title-bar > button:hover {
    background-color: var(--Editor-button-hover-background);
  }

  .title-bar > button:disabled {
    cursor: not-allowed;
    background-color: var(--Editor-button-disabled-background);
    color: var(--Editor-button-disabled-foreground);
  }

  #editor-reset-size {
    margin-right:4px;
  }

  .title-bar > input[type=number] {
    border: solid 0px;
    color: var(--Editor-input-foreground);
    background-color: var(--Editor-input-background);
    font-size: 12pt;
    margin: 4px 0 4px 0;
    text-align: center;
    width: 48px;
  }

  /**
   * https://stackoverflow.com/questions/45396280/customizing-increment-arrows-on-input-of-type-number-using-css
   * Used to hide native arrows on input of type number.
   */
  input[type="number"] {
    -webkit-appearance: textfield;
      -moz-appearance: textfield;
            appearance: textfield;
  }

  input[type=number]::-webkit-inner-spin-button,
  input[type=number]::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }

</style>
